<template>
  <div
    class="search-product-card"
    @click="$emit('navigate', product)"
  >
    <div class="search-product-card__image">
      <img
        :src="thumbUrl"
        :alt="product.name"
        loading="lazy"
        @error="onImageError"
      >
    </div>

    <div class="search-product-card__info">
      <span class="search-product-card__brand">{{ product.brand }}</span>
      <span class="search-product-card__name">{{ product.name }}</span>
      <!-- Complete design number (base + color, e.g. MC20-1062A) — same as the catalog card. -->
      <span class="search-product-card__design">{{ product.design_id || product.design_number }}</span>

      <div class="search-product-card__meta">
        <span v-if="showPrice" class="search-product-card__price">
          <span :class="{ 'search-product-card__price--struck': product.special_price }">
            {{ formatPrice(product.regular_price) }}
          </span>
          <span v-if="product.special_price" class="search-product-card__price--sale">
            {{ formatPrice(product.special_price) }}
          </span>
        </span>

        <button
          v-if="hasSubmitOrdersPermission"
          type="button"
          class="search-product-card__order"
          :class="{ 'is-on-order': isOnOrder }"
          :disabled="!canOrderProduct(product)"
          @click.stop="onOrderClick"
        >
          <template v-if="isOnOrder">
            <fa icon="times" fixed-width /> On Order
          </template>
          <span v-else>Order</span>
        </button>
      </div>

      <!-- Same red availability / prebook line the catalog tiles show (availabilityNotice mixin). -->
      <div v-if="availabilityNotice(product)" class="search-product-card__notice">
        {{ availabilityNotice(product) }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import orderRelated from '~/mixins/orderRelated'
import productOrderable from '~/mixins/productOrderable'
import availabilityNotice from '~/mixins/availabilityNotice'
import wholesalePriceVisibility from '~/mixins/wholesalePriceVisibility'
import { formatUSDCurrency } from '~/helpers/format'
import { PERMISSIONS } from '~/constants'

const FALLBACK_IMAGE = '/img/images/image_not_available.png'

export default {
  name: 'SearchProductCard',

  // orderRelated → activeDraftOrder + doOrder (orders/addProduct|removeProduct → fetchOrders),
  // the exact add/remove chain the catalog card uses; productOrderable → canOrderProduct gate;
  // availabilityNotice → the red "Available …" / "Prebook: Start Ship …" line.
  mixins: [orderRelated, productOrderable, availabilityNotice, wholesalePriceVisibility],

  props: {
    product: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      // Optimistic on/off shown the instant the button is clicked, before the order
      // round-trip lands. null = defer to the real order (isInActiveOrder). Mirrors the
      // catalog grid, whose button flips immediately via the store's active_order flag.
      activeOverride: null
    }
  },

  computed: {
    ...mapGetters({
      permissions: 'auth/permissions'
    }),
    thumbUrl () {
      return this.product.image_variants?.thumb_webp ||
        this.product.image_variants?.thumb ||
        this.product.image_url ||
        FALLBACK_IMAGE
    },
    hasSubmitOrdersPermission () {
      return this.permissions?.includes(PERMISSIONS.SUBMIT_ORDERS)
    },
    showPrice () {
      return this.showWholesalePrice && this.permissions?.includes(PERMISSIONS.WHOLESALE_COST)
    },
    // Mirrors the catalog card's `product.active_order`: is this product already on the
    // active draft order? Derived from the order (source of truth) so the button reflects
    // the cart and flips after doOrder → fetchOrders, exactly like the grid.
    isInActiveOrder () {
      const lines = this.activeDraftOrder?.order_has_products || []
      return lines.some((line) => line.product_id === this.product.id)
    },
    // What the button renders: the optimistic override while a toggle is in flight,
    // otherwise the real order state.
    isOnOrder () {
      return this.activeOverride !== null ? this.activeOverride : this.isInActiveOrder
    }
  },

  methods: {
    formatPrice (value) {
      return formatUSDCurrency(value)
    },

    onImageError (event) {
      event.target.src = this.product.image_url || FALLBACK_IMAGE
    },

    // Identical to GridView's active_order branch → doOrder: toggles the product on/off the
    // active draft order (or opens the New Order modal when there is none). No business
    // logic is duplicated; it's the same mixin action the catalog uses. @click.stop keeps
    // it from also triggering the card's navigate.
    async onOrderClick () {
      if (!this.canOrderProduct(this.product) || !this.hasSubmitOrdersPermission) {
        return
      }

      const hadActiveOrder = !!this.activeDraftOrder
      // Toggle relative to what the user currently sees (the optimistic state), so a rapid
      // double-click flips correctly instead of fighting a stale server value.
      const current = this.isOnOrder
      const product = { ...this.product, active_order: current }
      const productOrder = this.activeDraftOrder
        ? { order_id: this.activeDraftOrder.id, product_ids: [product.id] }
        : { products: [{ product_id: product.id }] }

      // Flip the button instantly (only meaningful when there's an order to toggle within;
      // with no active order doOrder opens the New Order modal and nothing is added yet).
      if (hadActiveOrder) {
        this.activeOverride = !current
      }

      await this.doOrder({ index: 0, property: 'active_order', product, productOrder })

      // doOrder awaited fetchOrders, so isInActiveOrder is now authoritative — drop the
      // override (on error it reverts to the real state, exactly like the grid).
      this.activeOverride = null

      // The search dropdown is available on every screen. When it mutates the active order
      // while the user is inside the checkout flow, that flow's data is now stale: the
      // order-summary list is a separate `checkout` fetch, and the later steps (review /
      // delivery / payment) were computed from a cart that just changed. Re-sync it.
      if (hadActiveOrder) {
        this.syncCheckoutFlow()
      }
    },

    // On order-summary: refresh its product list so the add/remove shows without a manual
    // reload. On any later checkout step: bounce back to order-summary, because changing the
    // cart invalidates the review/delivery/payment the user was on.
    syncCheckoutFlow () {
      const name = this.$route.name
      if (!name || !name.startsWith('checkout-proceed')) {
        return
      }
      if (name === 'checkout-proceed.order-summary') {
        this.$store.dispatch('checkout/fetchProductItemsOrderSummary')
      } else {
        this.$router.push({ name: 'checkout-proceed.order-summary' })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.search-product-card {
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &__image {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    background: #f6f6f6;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.25s ease;
    }
  }

  &:hover &__image img {
    transform: scale(1.06);
  }

  &__info {
    display: flex;
    flex-direction: column;
    padding-top: 8px;
    font: normal normal normal 12px/16px Arial;
    color: #383838;
  }

  &__brand {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__name {
    color: #6b6b6b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__design {
    color: #9a9a9a;
    font-size: 11px;
  }

  &__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
    gap: 8px;
  }

  &__price {
    display: inline-flex;
    gap: 6px;
    align-items: baseline;

    &--struck {
      text-decoration: line-through;
      color: #9a9a9a;
    }

    &--sale {
      color: #d0021b;
      font-weight: 700;
    }
  }

  &__order {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid #383838;
    background: #fff;
    color: #383838;
    font: normal normal 700 11px/1 Arial;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 6px 12px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: #383838;
      color: #fff;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    // Already on the active order — solid black, like the catalog's "On Order" state.
    &.is-on-order {
      background: #383838;
      color: #fff;
    }
  }

  &__notice {
    margin-top: 6px;
    color: #d0021b;
    font: normal normal 700 11px/14px Arial;
  }
}
</style>
